import { Component, PropsWithChildren, ReactNode, memo } from 'react'

import { Outlet } from '@tanstack/react-router'

import { ErrorDisplay } from './ErrorDisplay'

type FallbackProps = {
  error: unknown
  resetError: () => void
}

type ErrorBoundaryProps = PropsWithChildren<{
  fallback?: ReactNode | ((props: FallbackProps) => ReactNode)
}>

type ErrorBoundaryState = {
  hasError: boolean
  error: unknown
}

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: undefined }
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.error(error)
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props

      if (typeof fallback === 'function') {
        return fallback({ error: this.state.error, resetError: this.resetError })
      }

      if (fallback !== undefined) {
        return fallback
      }

      return <ErrorDisplay error={this.state.error} />
    }

    return this.props.children ?? <Outlet />
  }
}

const ErrorBoundaryRaw = ({ children, fallback }: ErrorBoundaryProps) => {
  return <ErrorBoundaryClass fallback={fallback}>{children}</ErrorBoundaryClass>
}

export const ErrorBoundary = memo(ErrorBoundaryRaw)
